import React, { Component } from "react";
import PropTypes from "prop-types";
import DropDownButton from "components/atoms/DropDownButton";
import ToolTip from "components/atoms/ToolTip";
import FilterBox from "components/molecules/FilterBox";
import makeToggleable from "hoc/toggleable";
import makePersistMountedState from "hoc/mountedState";
import makeScrolling from "hoc/scrolling";
import ShowHide from "containers/behavior/ShowHide";
import Panel from "components/molecules/Panel";
import classnames from "classnames";
import { isDeepEqual } from "utils/predicates";

@makePersistMountedState
@makeToggleable({ toggleProp: "tooltipIsActive" })
@makeToggleable({ toggleProp: "dropdownIsActive" })
@makeScrolling({ refName: "container" })
export default class OptionFilter extends Component {
  static propTypes = {
    currentValues: PropTypes.array.isRequired,
    className: PropTypes.string,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        count: PropTypes.number,
      }),
    ).isRequired,
    dropdownIsActive: PropTypes.bool,
    forceActive: PropTypes.bool,
    isMultiSelectFilter: PropTypes.bool,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onFilterChange: PropTypes.func.isRequired,
    onToggleDropdownIsActive: PropTypes.func,
    onToggleTooltipIsActive: PropTypes.func,
    scrollToTop: PropTypes.func,
    tooltipIsActive: PropTypes.bool,
  };

  static defaultProps = {
    currentValues: [],
    data: [],
    forceActive: false,
  };

  state = {
    selectedValues: this.props.currentValues,
    prevCurrentValues: this.props.currentValues,
    dropDownBtn: null,
  };

  static getDerivedStateFromProps(props, state) {
    if (!isDeepEqual(props.currentValues, state.prevCurrentValues)) {
      return {
        selectedValues: props.currentValues,
        prevCurrentValues: props.currentValues,
      };
    }
    return state;
  }

  handleClose = () => {
    if (this.state.dropDownBtn) {
      this.state.dropDownBtn.setAttribute("aria-expanded", "false");
      this.setState({ dropDownBtn: null });
    }
    this.props.onToggleDropdownIsActive(false);
    document.removeEventListener("mousedown", this.handleClickOutside);
    document.removeEventListener("keydown", this.handleEscapeKey);
  };

  handleEscapeKey = (event) => {
    if (event.key === "Escape" || event.key === "Esc") {
      this.handleClose();
    }
  };

  closeAllFilters = () => {
    const escapeEvent = new KeyboardEvent("keydown", {
      key: "Escape",
      code: "Escape",
      keyCode: 27,
      which: 27,
      bubbles: true,
      cancelable: true,
    });

    document.dispatchEvent(escapeEvent);
  };

  handleOpen = (dropDownBtn) => {
    this.setState({ dropDownBtn });
    dropDownBtn.setAttribute("aria-expanded", "true");
    this.closeAllFilters();
    this.props.scrollToTop();
    this.props.onToggleDropdownIsActive(true);
    document.addEventListener("mousedown", this.handleClickOutside);
    document.addEventListener("keydown", this.handleEscapeKey);
  };

  resetSelectedValues = () => {
    this.setState({ selectedValues: this.props.currentValues });
  };

  handleFilterValueChange = (values) => {
    this.setState({ selectedValues: values.values }, () => {
      const { selectedValues } = this.state;

      this.props.onFilterChange(this.props.name, selectedValues);
      if (!this.props.isMultiSelectFilter) {
        this.handleClose();
      }
    });
  };

  handleDropDownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.props.data.length === 0) {
      return;
    }
    const dropDownBtn = e.target.closest("button");

    if (this.props.dropdownIsActive) {
      this.resetSelectedValues();
      this.handleClose();
    } else {
      this.handleOpen(dropDownBtn);
    }
  };

  handleClickOutside = (ev) => {
    // eslint-disable-next-line react/no-string-refs
    const { container } = this.refs;
    const { target } = ev;

    if (target !== container && !container.contains(target)) {
      this.resetSelectedValues();
      this.handleClose();
    }
  };

  toggleTooltip = (e) => {
    if (
      (e.type === "mouseenter" || e.type === "mouseleave") &&
      e.target.contains(document.activeElement)
    ) {
      return false;
    } else if (
      e.type === "mouseleave" &&
      this.props.tooltipIsActive === false
    ) {
      return false;
    }

    const { onToggleTooltipIsActive } = this.props;

    if (this.props.tooltipIsActive) {
      onToggleTooltipIsActive(false);
    } else {
      onToggleTooltipIsActive(true);
    }
  };

  render() {
    const {
      data,
      name,
      label,
      dropdownIsActive,
      forceActive,
      tooltipIsActive,
      className,
      currentValues,
    } = this.props;

    const { selectedValues } = this.state;

    const componentBaseClass = "sal-option-filter";
    const hasData = data.length > 0;
    const componentClassName = classnames(componentBaseClass, className, {
      "sal-option-filter--hidden": !hasData && !forceActive,
    });
    const dropdownButtonClass = `${componentBaseClass}__button-dropdown`;

    let dropDownClassName = dropdownIsActive
      ? `${dropdownButtonClass} ${dropdownButtonClass}--active`
      : dropdownButtonClass;

    return (
      // eslint-disable-next-line react/no-string-refs
      <div className={componentClassName} ref="container">
        {!hasData ? (
          <div
            className={`${componentBaseClass}__button-wrapper`}
            onMouseEnter={this.toggleTooltip}
            onMouseLeave={this.toggleTooltip}
            onFocus={this.toggleTooltip}
            onBlur={this.toggleTooltip}
          >
            <DropDownButton
              name={name}
              value="open"
              onClick={this.handleDropDownClick}
              aria-expanded="false"
              aria-disabled={!hasData}
              aria-describedby={!hasData ? `${name}__tooltip` : null}
              caption={label}
              hasSelectedValue={currentValues && currentValues.length > 0}
              className={dropDownClassName}
              dropdownActive={dropdownIsActive}
            />
          </div>
        ) : (
          <DropDownButton
            name={name}
            value="open"
            onClick={this.handleDropDownClick}
            aria-expanded="false"
            aria-disabled={!hasData}
            aria-describedby={!hasData ? `${name}__tooltip` : null}
            caption={label}
            hasSelectedValue={currentValues && currentValues.length > 0}
            className={dropDownClassName}
            dropdownActive={dropdownIsActive}
          />
        )}
        {!hasData && (
          <ToolTip
            id={`${name}__tooltip`}
            group="filter"
            isActive={tooltipIsActive}
            position="top"
            className={`${componentBaseClass}__tooltip`}
          >
            In dieser Filterkombination nicht wählbar.
          </ToolTip>
        )}
        {
          <ShowHide
            isVisible={dropdownIsActive}
            animationType="slideDown"
            className={componentBaseClass + "__dropdown-container"}
            render={({ isVisible }) => (
              <FilterBox
                className={componentBaseClass + "__filterbox"}
                selectedValues={selectedValues}
                currentValues={currentValues}
                onCloseClick={this.handleClose}
                renderName="panel"
                hasBoxShadow={isVisible}
                render={(additionalProps) => (
                  <Panel
                    name={name}
                    data={data}
                    onFilterValueChange={this.handleFilterValueChange}
                    selectedValues={selectedValues}
                    isMultiSelectFilter={this.props.isMultiSelectFilter}
                    {...additionalProps}
                  />
                )}
              />
            )}
          />
        }
      </div>
    );
  }
}
